import { Code, Dialog, Typography } from '@neo4j-ndl/react';
import { CheckIconOutline, Square2StackIconOutline } from '@neo4j-ndl/react/icons';
import { useCopyWithMessage } from '@nx/ui';

interface BtreeIndexQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const query = `CALL {
  CALL db.indexes()
  YIELD name, type, entityType, labelsOrTypes, properties
  WHERE type = "RANGE" OR type = "TEXT" OR type = "POINT"
  RETURN entityType, labelsOrTypes, properties
  UNION
  RETURN "" AS entityType, [] AS labelsOrTypes, [] AS properties
}
WITH collect({entityType: entityType, labelsOrTypes: labelsOrTypes, properties: properties}) as collectionRTPIndexes
CALL {
  CALL db.constraints()
  YIELD name
  RETURN collect(name) AS collectionConstraintNames
}
CALL db.indexes()
YIELD name AS oldName, type, entityType AS oldEntityType, labelsOrTypes AS oldLabelsOrTypes, properties AS oldProperties
WHERE NOT {entityType: oldEntityType, labelsOrTypes: oldLabelsOrTypes, properties: oldProperties} IN collectionRTPIndexes AND type = "BTREE"
RETURN oldName, oldName IN collectionConstraintNames as constraintBacking, type, oldEntityType, oldLabelsOrTypes, oldProperties`;

export const BtreeIndexQueryModal = ({ isOpen, onClose }: BtreeIndexQueryModalProps) => {
  const [copyMessage, copyWithFeedback] = useCopyWithMessage();

  return (
    <Dialog size="large" isOpen={isOpen} onClose={onClose}>
      <Dialog.Header>Cypher query to find indexes without a replacement</Dialog.Header>
      <Dialog.Content>
        <div className="flex flex-col gap-3">
          <Code
            language="cypher"
            code={query}
            actions={[
              {
                ariaLabel: 'Copy to clipboard',
                htmlAttributes: { title: 'Copy to clipboard' },
                size: 'small',
                children: /copied/i.test(copyMessage ?? '') ? <CheckIconOutline /> : <Square2StackIconOutline />,
                onClick: () => {
                  void copyWithFeedback(query);
                },
              },
            ]}
          />
          <p>
            Indexes that are backing constraints have <Typography variant="code">constraintBacking: true</Typography> in
            the query output. Be aware that replacement indexes for them can only be RANGE indexes. Indexes of a
            different kind will not be correctly picked up by the migration process. This will lead to a longer
            migration time as well as an unused index afterwards.
          </p>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
